import { io } from 'socket.io-client';
import { getApiOrigin } from '../utils/assetUrls';

// Socket.IO connects to the site origin (not /api). With REACT_APP_API_URL=/api,
// use window.location.origin — stripping "/api" alone would wrongly fall back to localhost.
const SOCKET_URL =
  typeof window !== 'undefined' ? getApiOrigin() : 'http://localhost:3000';

const SOCKET_DEBUG =
  typeof window !== 'undefined' && window.localStorage?.getItem('DEBUG_SOCKET') === '1';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    /** Last room joins we want to restore after reconnect */
    this._desiredPracticeId = null;
    this._desiredPatient = null;
    this._lastPracticeEmit = null;
    this._lastPatientEmit = null;
  }

  /**
   * Connect to Socket.IO server
   */
  connect() {
    if (this.socket?.connected) {
      return this.socket;
    }

    if (this.socket) {
      try {
        this.socket.connect();
      } catch {
        /* ignore */
      }
      return this.socket;
    }

    // Start with HTTP long-polling and let Socket.IO upgrade to WebSocket
    // once it's negotiated. This is far more resilient behind reverse proxies
    // (nginx/ALB) that may not be configured to forward the WS Upgrade header,
    // and avoids the "ws://.../socket.io/?... failed" loop on staging.
    this.socket = io(SOCKET_URL, {
      transports: ['polling', 'websocket'],
      upgrade: true,
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      timeout: 20000
    });

    this.socket.on('connect', () => {
      if (SOCKET_DEBUG) console.log('Socket.IO connected:', this.socket.id);
      this.isConnected = true;
      if (this._desiredPracticeId != null) {
        this.socket.emit('join-practice-room', this._desiredPracticeId);
        this._lastPracticeEmit = String(this._desiredPracticeId);
      }
      if (this._desiredPatient) {
        this.socket.emit('join-patient-room', this._desiredPatient);
        this._lastPatientEmit = `${this._desiredPatient.practiceId}-${this._desiredPatient.patientId}`;
      }
    });

    this.socket.on('disconnect', () => {
      if (SOCKET_DEBUG) console.log('Socket.IO disconnected');
      this.isConnected = false;
      this._lastPracticeEmit = null;
      this._lastPatientEmit = null;
    });

    this.socket.on('connect_error', (error) => {
      // err.code on "server error" is the Engine.IO error code (see server logs:
      // [Socket.IO engine connection_error]).
      const detail = {
        message: error?.message,
        code: error?.code,
        description: error?.description,
        context: error?.context,
        type: error?.type
      };
      if (SOCKET_DEBUG) {
        console.error('Socket.IO connection error (detail):', detail, error);
      } else {
        console.error('Socket.IO connection error:', error?.message || error, detail);
      }
      this.isConnected = false;
    });

    return this.socket;
  }

  /**
   * Disconnect from Socket.IO server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this._desiredPracticeId = null;
      this._desiredPatient = null;
      this._lastPracticeEmit = null;
      this._lastPatientEmit = null;
    }
  }

  /**
   * Join patient room for real-time updates
   * @param {number} practiceId - Practice ID
   * @param {number} patientId - Patient ID (route param; server resolves room)
   */
  joinPatientRoom(practiceId, patientId) {
    if (practiceId == null || patientId == null) return;
    const payload = { practiceId, patientId };
    this._desiredPatient = payload;
    const key = `${practiceId}-${patientId}`;
    this.connect();

    if (this.socket?.connected) {
      if (this._lastPatientEmit === key) return;
      this.socket.emit('join-patient-room', payload);
      this._lastPatientEmit = key;
      if (SOCKET_DEBUG) console.log(`Joined patient room: practice-${practiceId}-patient-${patientId}`);
    }
  }

  /**
   * Join practice room for all patients updates
   * @param {number} practiceId - Practice ID
   */
  joinPracticeRoom(practiceId) {
    if (practiceId == null) return;
    this._desiredPracticeId = practiceId;
    const key = String(practiceId);
    this.connect();

    if (this.socket?.connected) {
      if (this._lastPracticeEmit === key) return;
      this.socket.emit('join-practice-room', practiceId);
      this._lastPracticeEmit = key;
      if (SOCKET_DEBUG) console.log(`Joined practice room: practice-${practiceId}`);
    }
  }

  /**
   * Stop tracking patient room (e.g. navigate away). Server still drops on disconnect.
   */
  leavePatientRoom(practiceId, patientId) {
    this._desiredPatient = null;
    this._lastPatientEmit = null;
    if (this.socket?.connected) {
      this.socket.emit('leave-patient-room', { practiceId, patientId });
    }
  }

  /**
   * Listen for vital reading updates
   * @param {Function} callback - Callback function to handle updates
   */
  onVitalReadingUpdate(callback) {
    if (!this.socket) {
      this.connect();
    }

    if (this.socket) {
      this.socket.on('vital-reading-update', (data) => {
        if (SOCKET_DEBUG) console.log('Received vital reading update:', data);
        callback(data);
      });
    }
  }

  /**
   * Listen for patient vital updates (for practice room)
   * @param {Function} callback - Callback function to handle updates
   */
  onPatientVitalUpdate(callback) {
    if (!this.socket) {
      this.connect();
    }

    if (this.socket) {
      this.socket.on('patient-vital-update', (data) => {
        if (SOCKET_DEBUG) console.log('Received patient vital update:', data);
        callback(data);
      });
    }
  }

  /**
   * Remove vital reading update listener
   */
  offVitalReadingUpdate() {
    if (this.socket) {
      this.socket.off('vital-reading-update');
    }
  }

  /**
   * Remove patient vital update listener
   */
  offPatientVitalUpdate() {
    if (this.socket) {
      this.socket.off('patient-vital-update');
    }
  }

  /**
   * Get socket instance
   */
  getSocket() {
    if (!this.socket?.connected) {
      this.connect();
    }
    return this.socket;
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;

